import { mcLogger } from '@/utils';
import { ReceivedMeals, ServingLine } from '@/models';
import { MealCountDtoViewModel } from '@/view-models';

export class MealCountCheck {
  /** Fancy colored logger */
  private static console: Console = mcLogger.getConsole('MealCountCheck', mcLogger.colors.MealCountCheck);

  static isDirty(notDirty: MealCountDtoViewModel, possiblyDirty: MealCountDtoViewModel): boolean {
    
    let cleanReceive = new ReceivedMeals();
    let cleanLine = new ServingLine();

    if (!notDirty) {
      cleanReceive.mealCountReceived = 0;
      cleanReceive.mealDeliveryTemperature = 0;
      cleanReceive.mealDeliveryDateTime = '';
      cleanReceive.mealCountLeftoverPrevious = 0;
      cleanLine.mealCountServedFirstMeal = 0;
      cleanLine.mealCountServedSecondMeal = 0;
      cleanLine.mealCountServedAdultProgram = 0;
      cleanLine.mealCountServedAdultNonProgram = 0;
      cleanLine.mealCountAdditionalRequested = 0;
      cleanLine.mealCountWaste = 0;
      cleanLine.mealCountIncomplete = 0;
    }
    else {
      cleanReceive.mealCountReceived = notDirty.receivedMeals.mealCountReceived;
      cleanReceive.mealDeliveryTemperature = notDirty.receivedMeals.mealDeliveryTemperature;
      cleanReceive.mealDeliveryDateTime = notDirty.receivedMeals.mealDeliveryDateTime;
      cleanReceive.mealCountLeftoverPrevious = notDirty.receivedMeals.mealCountLeftoverPrevious;
      cleanLine.mealCountServedFirstMeal = notDirty.userServingLine?.mealCountServedFirstMeal ?? 0;
      cleanLine.mealCountServedSecondMeal = notDirty.userServingLine?.mealCountServedSecondMeal ?? 0;
      cleanLine.mealCountServedAdultProgram = notDirty.userServingLine?.mealCountServedAdultProgram ?? 0;
      cleanLine.mealCountServedAdultNonProgram = notDirty.userServingLine?.mealCountServedAdultNonProgram ?? 0;
      cleanLine.mealCountAdditionalRequested = notDirty.userServingLine?.mealCountAdditionalRequested ?? 0;
      cleanLine.mealCountWaste = notDirty.userServingLine?.mealCountWaste ?? 0;
      cleanLine.mealCountIncomplete = notDirty.userServingLine?.mealCountIncomplete ?? 0;
      cleanLine.mealCountNonCongregateMeal = notDirty.userServingLine?.mealCountNonCongregateMeal ?? 0;
    }

    //this.logDirty(notDirty, possiblyDirty);

    const receiveIsDirty = this.isReceiveDirty(cleanReceive, possiblyDirty.receivedMeals);

    const servingLine = possiblyDirty.userServingLine;

    let serveIsDirty = false;

    if (servingLine) {
      serveIsDirty = this.isLineDirty(cleanLine, servingLine);
    }

    return receiveIsDirty || serveIsDirty;
  }

  static isAffirmDirty(notDirty: ServingLine | undefined, possiblyDirty: ServingLine | undefined) {
    const notDirtyAffirmed = notDirty ? notDirty.affirmed : false;
    const possiblyDirtyAffirmed = possiblyDirty ? possiblyDirty.affirmed : false;
    return notDirtyAffirmed !== possiblyDirtyAffirmed;
  }

  static isReceiveDirty(notDirty: ReceivedMeals, possiblyDirty: ReceivedMeals): boolean {

    const notDirtyDate = this.converteDateToISO(notDirty.mealDeliveryDateTime);

    const possiblyDirtyDate = this.converteDateToISO(possiblyDirty.mealDeliveryDateTime);

    return notDirty.mealCountLeftoverPrevious !== possiblyDirty.mealCountLeftoverPrevious ||
      notDirty.mealCountReceived !== possiblyDirty.mealCountReceived ||
      notDirty.mealDeliveryTemperature !== possiblyDirty.mealDeliveryTemperature; //||
      //notDirtyDate !== possiblyDirtyDate;
  }

  static isLineDirty(notDirty: ServingLine, possiblyDirty: ServingLine): boolean {
    
    const isServeDirty: boolean = notDirty.mealCountServedFirstMeal !== possiblyDirty.mealCountServedFirstMeal
    || notDirty.mealCountServedSecondMeal !== possiblyDirty.mealCountServedSecondMeal
    || notDirty.mealCountServedAdultProgram !== possiblyDirty.mealCountServedAdultProgram
    || notDirty.mealCountServedAdultNonProgram !== possiblyDirty.mealCountServedAdultNonProgram
    || notDirty.mealCountAdditionalRequested !== possiblyDirty.mealCountAdditionalRequested
    || notDirty.mealCountWaste !== possiblyDirty.mealCountWaste
    || notDirty.mealCountIncomplete !== possiblyDirty.mealCountIncomplete
    || notDirty.affirmed !== possiblyDirty.affirmed
    || notDirty.mealCountNonCongregateMeal !== possiblyDirty.mealCountNonCongregateMeal;

    return isServeDirty;
  }

  static logDirty(notDirty: MealCountDtoViewModel, possiblyDirty: MealCountDtoViewModel): void {
    if (!notDirty){
      console.log('Not Dirty Meal Count: ', notDirty);
      return;
    }

    console.table([
      {
      Field: 'mealCountReceived',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty?.receivedMeals?.mealCountReceived,
      PossiblyDirty: possiblyDirty?.receivedMeals?.mealCountReceived,
      IsSame: notDirty?.receivedMeals?.mealCountReceived === possiblyDirty?.receivedMeals?.mealCountReceived
      },
      {
      Field: 'mealDeliveryTemperature',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty?.receivedMeals?.mealDeliveryTemperature,
      PossiblyDirty: possiblyDirty?.receivedMeals?.mealDeliveryTemperature,
      IsSame: notDirty?.receivedMeals?.mealDeliveryTemperature === possiblyDirty?.receivedMeals?.mealDeliveryTemperature
      },
      {
      Field: 'mealDeliveryDateTime',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: this.converteDateToISO(notDirty?.receivedMeals?.mealDeliveryDateTime),
      PossiblyDirty: this.converteDateToISO(possiblyDirty?.receivedMeals?.mealDeliveryDateTime),
      IsSame: this.converteDateToISO(notDirty?.receivedMeals?.mealDeliveryDateTime) === 
      this.converteDateToISO(possiblyDirty?.receivedMeals?.mealDeliveryDateTime)
      },
      {
      Field: 'mealCountLeftoverPrevious',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.receivedMeals?.mealCountLeftoverPrevious,
      PossiblyDirty: possiblyDirty.receivedMeals?.mealCountLeftoverPrevious,
      IsSame: notDirty.receivedMeals?.mealCountLeftoverPrevious === possiblyDirty.receivedMeals?.mealCountLeftoverPrevious
      },
      {
      Field: 'mealCountServedFirstMeal',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountServedFirstMeal ?? 0,
      PossiblyDirty: possiblyDirty?.userServingLine?.mealCountServedFirstMeal ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountServedFirstMeal ?? 0) === (possiblyDirty.userServingLine?.mealCountServedFirstMeal ?? 0)
      },
      {
      Field: 'mealCountServedSecondMeal',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountServedSecondMeal ?? 0,
      PossiblyDirty: possiblyDirty?.userServingLine?.mealCountServedSecondMeal ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountServedSecondMeal ?? 0) === (possiblyDirty.userServingLine?.mealCountServedSecondMeal ?? 0)
      },
      {
      Field: 'mealCountServedAdultProgram',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountServedAdultProgram ?? 0,
      PossiblyDirty: possiblyDirty?.userServingLine?.mealCountServedAdultProgram ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountServedAdultProgram ?? 0) === (possiblyDirty.userServingLine?.mealCountServedAdultProgram ?? 0)
      },
      {
      Field: 'mealCountServedAdultNonProgram',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountServedAdultNonProgram ?? 0,
      PossiblyDirty: possiblyDirty?.userServingLine?.mealCountServedAdultNonProgram ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountServedAdultNonProgram ?? 0) === (possiblyDirty.userServingLine?.mealCountServedAdultNonProgram ?? 0)
      },
      {
      Field: 'mealCountAdditionalRequested',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountAdditionalRequested ?? 0,
      PossiblyDirty: possiblyDirty?.userServingLine?.mealCountAdditionalRequested ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountAdditionalRequested ?? 0) === (possiblyDirty.userServingLine?.mealCountAdditionalRequested ?? 0)
      },
      {
      Field: 'mealCountWaste',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountWaste ?? 0,
      PossiblyDirty: possiblyDirty.userServingLine?.mealCountWaste ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountWaste ?? 0) === (possiblyDirty.userServingLine?.mealCountWaste ?? 0)
      },
      {
      Field: 'mealCountIncomplete',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountIncomplete ?? 0,
      PossiblyDirty: possiblyDirty.userServingLine?.mealCountIncomplete ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountIncomplete ?? 0) === (possiblyDirty.userServingLine?.mealCountIncomplete ?? 0)
      },
      {
      Field: 'mealCountNonCongregateMeal',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.mealCountNonCongregateMeal ?? 0,
      PossiblyDirty: possiblyDirty.userServingLine?.mealCountNonCongregateMeal ?? 0,
      IsSame: (notDirty.userServingLine?.mealCountNonCongregateMeal ?? 0) === (possiblyDirty.userServingLine?.mealCountNonCongregateMeal ?? 0)
      },
      {
      Field: 'affirmed',
      MealType: possiblyDirty?.mealType ?? 'Unknown',
      NotDirty: notDirty.userServingLine?.affirmed ?? false,
      PossiblyDirty: possiblyDirty.userServingLine?.affirmed ?? false,
      IsSame: (notDirty.userServingLine?.affirmed ?? false) === (possiblyDirty.userServingLine?.affirmed ?? false)
      }
    ]);
  }

  static converteDateToISO(date: string | undefined): string | undefined {
    return date && !date.endsWith('Z') 
      ? new Date(date).toISOString() 
      : date;
    }
}
